<template>
    <v-row
        no-gutters
        class="pr-8 pl-6 pt-8"
    >
        <v-col cols="12">
            <AppStoreListHeader
                :item-label="$t('Email templates')"
                :add-label="$t('Add')"
                show-button
                @add-item="onEditTemplate(null)"
            />
            <v-row
                v-if="emailAutomationTemplates.length > 0"
                no-gutters
            >
                <v-col cols="12">
                    <data-table
                        :items="emailAutomationTemplates"
                        :headers="headers"
                        :loading="tableLoader.isLoading"
                        row-clickable
                        @click:row="onEditTemplate($event.id)"
                    >
                        <template v-slot:item.locale_code="{ item }">
                            {{ item.rows.map(x => formatLanguageLabel({name: x.locale_code.substr(0, 2), code: x.locale_code})).join(', ').toUpperCase() }}
                        </template>
                        <template v-slot:item.is_public="{ item }">
                            {{ item.is_public ? $t('Everyone') : $t('Only me') }}
                        </template>
                        <template v-slot:item.creator="{ item }">
                            {{ `${item.creator.name} ${item.creator.surname}` }}
                        </template>
                    </data-table>
                </v-col>
            </v-row>
        </v-col>
        <EditEmailTemplateDialog
            v-if="template"
            :template="template"
            :errors="errors"
            :form_data="form_data"
            :show-dialog="Boolean(template)"
            :loader="editorLoader"
            :company-id="selectedAccount.id"
            :header-text="template.id ? $t('Edit email template') : $t('Add email template')"
            :show-placeholders="true"
            :type="CONTENT_TEMPLATE_TYPE.EMAIL_AUTOMATION"
            :channel="CONTENT_TEMPLATE_CHANNEL.EMAIL"
            @cancel="onCancelTemplate"
            @save="onSaveTemplate"
            @delete="onDeleteTemplate"
            @attachmentUploadChange="uploadChange"
            @attachmentDeleteFile="fileUpload.delete_file"
            @sendTestEmail="sendTestEmailOut"
        />
    </v-row>
</template>

<script>
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import EditTemplate from "../EditTemplate/Index";
import EmailAutomationModule from "../../../store/modules/email_automation"
import AccountModule from "../../../store/modules/account"
import { ref, computed, reactive } from "@vue/composition-api";
import { $t } from "@/i18n";
import { CONTENT_TEMPLATE_TYPE, CONTENT_TEMPLATE_CHANNEL } from "../../../helpers/constants";
import FileUpload from "../../../composables/FileUpload";
import DialogModule from "../../../store/modules/dialog";
import EditEmailTemplateDialog from "../../Dialog/EditEmailTemplateDialog/Index"
import Loader from "../../../utility/loader"
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {formatLanguageLabel} from '../../../helpers/formatting'
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "EmailingTemplatesTab",
    methods: {formatLanguageLabel},
    components: {
        AppStoreListHeader,
        EditTemplate,
        EditEmailTemplateDialog
    },
    setup(props, ctx) {
        const {
            setConfirmOptions,
            openConfirmDialog,
        } = DialogModule();
        const { appStoreDialogFlags, appStoreDialogGetters } = appStoreDialog()
        const { emailAutomationTemplates, fetchEmailAutomationTemplates, saveEmailAutomationTemplate, fetchEmailAutomationTemplate, deleteEmailAutomationTemplate } = EmailAutomationModule()
        const { current_account } = AccountModule();
        const template = ref(null);
        const errors = ref({});
        const task_data = ref(null)
        const form_data = ref(null)
        const tableLoader = reactive(new Loader())
        const editorLoader = reactive(new Loader())

        const editingTemplateId = ref(null);

        const { sendTestEmail } = EmailAutomationModule()
        const { showFeedbackNotification } = FeedbackNotificationModule();

        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const headers = ref([
            {
                text: $t("Name"),
                value: "template_name",
                sortable: false,
                width: '50%'
            },
            {
                text: $t("Language"),
                value: "locale_code",
                sortable: false
            },
            {
                text: $t("Access"),
                value: "is_public",
                sortable: false
            },
            {
                text: $t("Creator"),
                value: "creator",
                sortable: false
            },
            {
                value: "edit"
            }
        ])

        const fetchData = async function() {
            tableLoader.withLoader(async () => {
                try {
                    await fetchEmailAutomationTemplates(selectedAccount.value.id);
                } catch (e) {
                    errorDialog.showError(e);
                }
            })
        }

        const onEditTemplate = async (id) => {
            if (id) {
                template.value = await fetchEmailAutomationTemplate(id)
            } else {
                template.value = {
                    account_id: selectedAccount.value.id,
                    is_public: false,
                    template_name: '',
                    rows: [],
                    files: [],
                    creator_id: current_account.value.id,
                    type: CONTENT_TEMPLATE_TYPE.EMAIL_AUTOMATION,
                    channel: CONTENT_TEMPLATE_CHANNEL.EMAIL
                }
            }

            task_data.value = {
                account_id: selectedAccount.value.id,
                id: id,
                files: template.value.files
            }
            form_data.value = {
                files: template.value.files,
                account_id: selectedAccount.value.id,
                id: id,
            }
        }

        const fileDeleted = (deletedId) => {
            form_data.value.files = form_data.value.files.filter(x => x.id !== deletedId);
            task_data.value.files = task_data.value.files.filter(x => x.id !== deletedId);
        }
        const fileUploaded = (resp, file) => {
            if (template.value.id) {
                const index = form_data.value.files.findIndex(x => x.file_name === resp.file_name)
                form_data.value.files[index] = resp;
            }
        }
        const fileUpload = FileUpload("email_automation_template", task_data, form_data, false, fileUploaded, fileDeleted);
        const uploadChange = (e) => {
            fileUpload.add_files_to_upload(e.target.files, true);
        };

        const onCancelTemplate = () => {
            template.value = null
        }

        const forceSaveTemplate = async (saveTemplate) => {
            try {
                await saveEmailAutomationTemplate(saveTemplate, true)
                await fetchData()
                template.value = null;
            } catch (error) {
                errors.value = error
            }
        }

        const onForceSaveTemplate = (saveTemplate) => {
            setConfirmOptions({
                title: $t("Are you sure you want to save this template? Some tasks will be affected"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => forceSaveTemplate(saveTemplate)
            });
            openConfirmDialog();
        }

        const onSaveTemplate = async (saveTemplate) => {
            errors.value = {}
            editorLoader.withLoader(async () => {
                try {
                    const { data } = await saveEmailAutomationTemplate(saveTemplate, false)
                    if (data.tasks_affected) {
                        onForceSaveTemplate(saveTemplate)
                    } else {
                        await fetchData()
                        template.value = null;
                    }

                } catch (error) {
                    console.log(error)
                    errors.value = error
                }
            })
        }

        const forceDeleteTemplate = async () => {
            try {
                await deleteEmailAutomationTemplate(template.value.id, 1)
                await fetchData()
                template.value = null
            } catch (error) {
                errorDialog.showError(error)
            }
        }

        const onForceDeleteTemplate = (tasks_affected) => {
            const title = tasks_affected ?
                $t("Are you sure you want to delete this template? Some tasks will be affected") :
                $t("Are you sure you want to delete this template?")

            setConfirmOptions({
                title: title,
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => forceDeleteTemplate()
            });
            openConfirmDialog();
        }

        const onDeleteTemplate = async () => {
            try {
                errors.value = {}
                const { data } = await deleteEmailAutomationTemplate(template.value.id, 0)

                onForceDeleteTemplate(data.tasks_affected)
            } catch (error) {
                errorDialog.showError(error)
            }
        }

        const sendTestEmailOut = async (template) => {
            try {
                await sendTestEmail(template)
                showFeedbackNotification($t('Test email sent'))
            } catch (error) {
                errors.value = error

            }
        }

        fetchData()

        return {
            headers,
            emailAutomationTemplates,
            appStoreDialogFlags,
            editingTemplateId,
            fetchData,
            onEditTemplate,
            template,
            onCancelTemplate,
            onSaveTemplate,
            errors,
            form_data,
            uploadChange,
            fileUpload,
            onDeleteTemplate,
            tableLoader,
            editorLoader,
            selectedAccount,
            CONTENT_TEMPLATE_TYPE,
            CONTENT_TEMPLATE_CHANNEL,
            sendTestEmailOut,
        };
    }
};
</script>
